import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, IconButton, InputLabel,
  MenuItem, Pagination, Paper, Select, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon    from '@mui/icons-material/Add';
import EditIcon   from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockResetIcon from '@mui/icons-material/LockReset';
import KeyIcon    from '@mui/icons-material/Key';
import {
  getOperators, getSupervisors, createOperator, updateOperator, deleteOperator,
  resetPin, setDashboardPassword, getLocations, getDepartments,
  getShifts, isAdmin, isSupervisor,
} from '../api/client';
import useDetectionSettings, { enabled } from '../hooks/useDetectionSettings';

// FastAPI can return detail as string OR as array of Pydantic validation objects
function extractError(err) {
  const detail = err.response?.data?.detail;
  if (!detail) return 'An error occurred';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => {
      const field = d.loc ? d.loc.slice(1).join('.') : '';
      return field ? `${field}: ${d.msg}` : d.msg;
    }).join('; ');
  }
  return JSON.stringify(detail);
}

const ROLES   = ['operator', 'supervisor'];
const STATUSES = ['active', 'inactive', 'disabled'];

// Admin currently can only create supervisor accounts — operators are
// created by supervisors under them. Flip this back to true if that
// requirement changes; it gates the Role field + default role below.
const ADMIN_CAN_CREATE_OPERATORS = false;

const statusColor = { active: 'success', inactive: 'warning', disabled: 'error' };
const roleColor   = { operator: 'primary', supervisor: 'warning', admin: 'error' };

const emptyForm = {
  employee_id: '', pin: '', dashboard_password: '',
  name: '', email: '', role: 'operator',
  location_id: '', supervisor_id: '', department_id: '',
  shift_id: '',
  break_start_delay_minutes: 1,
};

export default function OperatorsPage() {
  const detectionSettings = useDetectionSettings();
  const [operators,    setOperators]    = useState([]);
  const [locations,    setLocations]    = useState([]);
  const [departments,  setDepartments]  = useState([]);
  const [supervisors,  setSupervisors]  = useState([]);
  const [shifts,       setShifts]       = useState([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [statusFilter,  setStatusFilter]  = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  // Dialogs
  const [createOpen,   setCreateOpen]   = useState(false);
  const [editOpen,     setEditOpen]     = useState(false);
  const [pinOpen,      setPinOpen]      = useState(false);
  const [dashPwOpen,   setDashPwOpen]   = useState(false);
  const [selected,     setSelected]     = useState(null);

  const [form,         setForm]         = useState(emptyForm);
  const [newPin,       setNewPin]       = useState('');
  const [newDashPw,    setNewDashPw]    = useState('');
  const [saving,       setSaving]       = useState(false);
  const [formError,    setFormError]    = useState(null);

  const PAGE_SIZE = 50;
  const hasActiveShifts = Array.isArray(shifts) && shifts.some(shift => shift.is_active);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getOperators({
        status:      statusFilter  || undefined,
        location_id: locationFilter || undefined,
        page, page_size: PAGE_SIZE,
      });
      setOperators(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getShifts().then(result => {
      // The non-paginated endpoint returns an array.  Accept the paginated
      // envelope as well so older deployments (or a proxy adding pagination)
      // cannot leave the controlled Select with an object instead of options.
      const items = Array.isArray(result)
        ? result
        : (result?.items || result?.results || result?.data || []);
      setShifts(Array.isArray(items) ? items : []);
    }).catch(() => setShifts([]));
    // Load dropdowns (admins only for full lists, supervisors only need for display)
    if (isAdmin()) {
      getLocations().then(setLocations).catch(() => {});
      getDepartments().then(setDepartments).catch(() => {});
      // Load supervisors for assignment dropdown
      getSupervisors().then(setSupervisors).catch(() => {});
    }
  }, []);

  const openCreate = () => {
    if (!hasActiveShifts) return;
    setForm({ ...emptyForm, role: isAdmin() && !ADMIN_CAN_CREATE_OPERATORS ? 'supervisor' : 'operator' });
    setFormError(null);
    setCreateOpen(true);
  };
  const openEdit   = (op) => {
    setSelected(op);
    setForm({
      employee_id:        op.employee_id || '',
      pin:                '',
      dashboard_password: '',
      name:               op.name        || '',
      email:              op.email       || '',
      role:               op.role        || 'operator',
      location_id:        op.location_id || '',
      supervisor_id:      op.supervisor_id || '',
      department_id:      op.department_id || '',
      // Older operator responses exposed only the nested shift object.
      // Prefer the canonical FK and fall back to that nested representation.
      shift_id:           op.shift_id || op.shift?.id || '',
      break_start_delay_minutes: op.break_start_delay_minutes ?? 1,
    });
    setFormError(null);
    setEditOpen(true);
  };

  const handleCreate = async () => {
    setFormError(null);

    if (!hasActiveShifts) {
      setFormError('Create a Shift before adding Supervisors or Operators.'); return;
    }

    // Validation
    if (!form.employee_id || form.employee_id.length !== 4) {
      setFormError('Employee ID must be exactly 4 digits'); return;
    }
    if (!form.name.trim()) {
      setFormError('Name is required'); return;
    }
    if (!form.pin || form.pin.length < 4) {
      setFormError('PIN must be 4-8 digits'); return;
    }
    if (isAdmin() && form.role === 'supervisor') {
      if (!form.dashboard_password) {
        setFormError('Dashboard password is required for supervisors'); return;
      }
      if (!form.location_id) {
        setFormError('Please assign a location before creating a supervisor. Create a location first under the Locations page.'); return;
      }
    }

    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.dashboard_password) delete payload.dashboard_password;
      if (!payload.location_id)   delete payload.location_id;
      if (!payload.supervisor_id) delete payload.supervisor_id;
      if (!payload.department_id) delete payload.department_id;
      if (!payload.shift_id) delete payload.shift_id;
      await createOperator(payload);
      setCreateOpen(false);
      load();
      // Refresh supervisor list in case a new supervisor was just created
      if (isAdmin()) getSupervisors().then(setSupervisors).catch(() => {});
    } catch (e) {
      setFormError(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    setFormError(null);
    if (!hasActiveShifts) {
      setFormError('Create a Shift before adding Supervisors or Operators.'); return;
    }
    if (!form.shift_id) {
      setFormError('Please assign an active shift before saving.'); return;
    }
    setSaving(true);
    try {
      const payload = {};
      if (form.name)         payload.name         = form.name;
      if (form.email)        payload.email        = form.email;
      payload.shift_id = form.shift_id || '';
      if (isAdmin()) {
        if (form.role)         payload.role         = form.role;
        if (form.location_id)  payload.location_id  = form.location_id;
        if (form.supervisor_id) payload.supervisor_id = form.supervisor_id;
        if (form.department_id) payload.department_id = form.department_id;
        if (form.dashboard_password) payload.dashboard_password = form.dashboard_password;
      }
      await updateOperator(selected.id, payload);
      setEditOpen(false);
      load();
    } catch (e) {
      setFormError(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (op) => {
    if (!window.confirm(`Disable operator ${op.name}?`)) return;
    try {
      await deleteOperator(op.id);
      load();
    } catch (e) {
      setError(extractError(e));
    }
  };

  const handleActivate = async (op) => {
    try {
      await updateOperator(op.id, { status: 'active' });
      load();
    } catch (e) {
      setError(extractError(e));
    }
  };

  const handleResetPin = async () => {
    if (!newPin || newPin.length < 4) { setFormError('PIN must be 4-8 digits'); return; }
    setSaving(true);
    try {
      await resetPin(selected.id, newPin);
      setPinOpen(false);
      setNewPin('');
    } catch (e) {
      setFormError(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleSetDashPw = async () => {
    if (!newDashPw || newDashPw.length < 6) { setFormError('Password must be at least 6 chars'); return; }
    setSaving(true);
    try {
      await setDashboardPassword(selected.id, newDashPw);
      setDashPwOpen(false);
      setNewDashPw('');
      load();
    } catch (e) {
      setFormError(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>
          Operators
          {isSupervisor() && (
            <Typography component="span" variant="body2" color="text.secondary" ml={1}>
              (your team)
            </Typography>
          )}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status"
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          {isAdmin() && locations.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Location</InputLabel>
              <Select value={locationFilter || ''} label="Location"
                onChange={e => { setLocationFilter(e.target.value); setPage(1); }}>
                <MenuItem value="">All</MenuItem>
                {locations.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
            disabled={!hasActiveShifts}>
            {isAdmin() ? 'Add Supervisor' : 'Add Operator'}
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!hasActiveShifts && (
        <Alert severity="warning" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" variant="outlined" type="button"
            component={Link} to="/shifts">
            Go to Shift Management
          </Button>
        }>
          No shifts have been created yet. Please create a Shift before adding
          Supervisors or Operators.
        </Alert>
      )}

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Employee ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Shift</TableCell>
                <TableCell>Attendance</TableCell>
                {isAdmin() && <TableCell align="center">Operators</TableCell>}
                {enabled(detectionSettings, 'drowsiness') && <TableCell align="center">😴 Drowsy</TableCell>}
                {enabled(detectionSettings, 'yawning') && <TableCell align="center">🥱 Yawns</TableCell>}
                {enabled(detectionSettings, 'distracted') && <TableCell align="center">👀 Distracted</TableCell>}
                {enabled(detectionSettings, 'presence') && <TableCell align="center">📍 Presence</TableCell>}
                <TableCell>Dashboard Access</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {operators.map(op => (
                <TableRow key={op.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {op.employee_id}
                  </TableCell>
                  <TableCell>{op.name}</TableCell>
                  <TableCell>
                    <Chip label={op.role} color={roleColor[op.role] || 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={op.status} color={statusColor[op.status] || 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    {op.location_name || locations.find(l => l.id === op.location_id)?.name || '—'}
                  </TableCell>
                  <TableCell>
                    {op.shift ? <Box>
                      <Typography variant="body2">{op.shift.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {op.shift.start_time}–{op.shift.end_time} · {op.shift.timezone}
                      </Typography>
                    </Box> : 'Unassigned'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={
                      <Box>
                        <Typography variant="caption" display="block">First Check-In: {op.first_check_in ? new Date(op.first_check_in).toLocaleString() : '—'}</Typography>
                        <Typography variant="caption" display="block">Last Check-Out: {op.last_check_out ? new Date(op.last_check_out).toLocaleString() : '—'}</Typography>
                        <Typography variant="caption" display="block">Working Time: {op.total_working_seconds ? `${(op.total_working_seconds / 3600).toFixed(1)}h` : '—'}</Typography>
                        {op.presence_summary && <>
                          <Typography variant="caption" display="block">Away Time: {(op.presence_summary.away_seconds / 3600).toFixed(1)}h</Typography>
                          <Typography variant="caption" display="block">Break Time: {(op.presence_summary.approved_break_seconds / 3600).toFixed(1)}h</Typography>
                        </>}
                      </Box>
                    }>
                      <Chip size="small"
                        label={op.current_status === 'active' ? 'Active' : 'Offline'}
                        color={op.current_status === 'active' ? 'success' : 'default'} />
                    </Tooltip>
                  </TableCell>
                  {isAdmin() && (
                    <TableCell align="center">
                      <Chip label={op.operator_count ?? 0} size="small" variant="outlined" />
                    </TableCell>
                  )}
                  {enabled(detectionSettings, 'drowsiness') && <TableCell align="center">
                    <Chip label={op.total_drowsiness ?? 0} size="small" variant="outlined"
                      color={(op.total_drowsiness ?? 0) > 10 ? 'error' : (op.total_drowsiness ?? 0) > 0 ? 'warning' : 'default'} />
                  </TableCell>}
                  {enabled(detectionSettings, 'yawning') && <TableCell align="center">
                    <Chip label={op.total_yawns ?? 0} size="small" variant="outlined"
                      color={(op.total_yawns ?? 0) > 10 ? 'warning' : 'default'} />
                  </TableCell>}
                  {enabled(detectionSettings, 'distracted') && <TableCell align="center">
                    <Chip label={op.total_distracted ?? 0} size="small" variant="outlined"
                      sx={{ borderColor: '#9c27b0',
                            color: (op.total_distracted ?? 0) > 0 ? '#9c27b0' : 'text.secondary' }} />
                  </TableCell>}
                  {enabled(detectionSettings, 'presence') && <TableCell align="center">
                    {op.presence_summary ? `${op.presence_summary.presence_percent}%` : '—'}
                  </TableCell>}
                  <TableCell>
                    {op.role === 'supervisor' ? (
                      <Chip
                        label={op.has_dashboard_access ? 'Set' : 'Not set'}
                        color={op.has_dashboard_access ? 'success' : 'warning'}
                        size="small"
                      />
                    ) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(op)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {isAdmin() && (
                      <>
                        <Tooltip title="Reset PIN">
                          <IconButton size="small" onClick={() => {
                            setSelected(op); setFormError(null); setNewPin(''); setPinOpen(true);
                          }}>
                            <LockResetIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {op.role === 'supervisor' && (
                          <Tooltip title="Set Dashboard Password">
                            <IconButton size="small" onClick={() => {
                              setSelected(op); setFormError(null); setNewDashPw(''); setDashPwOpen(true);
                            }}>
                              <KeyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                    {/* Both admin and supervisor can disable/re-enable operators under them */}
                    {(isAdmin() || (isSupervisor() && op.role === 'operator')) && (
                      op.status === 'active' ? (
                        <Tooltip title="Disable">
                          <IconButton size="small" color="error" onClick={() => handleDelete(op)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Re-enable">
                          <IconButton size="small" color="success" onClick={() => handleActivate(op)}>
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {operators.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin() ? 12 : 11} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    No operators found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {total > PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={Math.ceil(total / PAGE_SIZE)}
            page={page}
            onChange={(_, v) => setPage(v)}
          />
        </Box>
      )}

      {/* ── Create dialog ── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isAdmin() ? 'Add Supervisor' : 'Add Operator'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Employee ID (4 digits)" required size="small"
              value={form.employee_id}
              onChange={e => setForm(f => ({ ...f, employee_id: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
              inputProps={{ maxLength: 4, inputMode: 'numeric' }}
            />
            <TextField
              label="Name" required size="small"
              value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            />
            <TextField
              label="Email" size="small" type="email"
              value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            />
            {/* Admin currently only creates supervisors (role locked, see
                ADMIN_CAN_CREATE_OPERATORS above); supervisors only create
                operators under them — role dropdown only needed when an
                admin is allowed to choose between the two. */}
            {isAdmin() && ADMIN_CAN_CREATE_OPERATORS && (
              <FormControl size="small">
                <InputLabel>Role</InputLabel>
                <Select value={form.role} label="Role"
                  onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <TextField
              label="PIN (4-8 digits)" required size="small"
              value={form.pin} type="password"
              onChange={e => setForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 8) }))}
              inputProps={{ maxLength: 8, inputMode: 'numeric' }}
              helperText="Used to log in on the edge device"
            />

            {/* Dashboard password — only show when creating a supervisor (admin only) */}
            {isAdmin() && form.role === 'supervisor' && (
              <TextField
                label="Dashboard Password" required size="small" type="password"
                value={form.dashboard_password}
                onChange={e => setForm(f => ({ ...f, dashboard_password: e.target.value }))}
                helperText="Required for supervisor dashboard login"
              />
            )}
            <FormControl size="small" required>
              <InputLabel>Shift</InputLabel>
              <Select value={form.shift_id} label="Shift"
                onChange={e => setForm(f => ({ ...f, shift_id: e.target.value }))}>
                <MenuItem value="">Unassigned</MenuItem>
                {shifts.filter(s => s.is_active).map(s => <MenuItem key={s.id} value={s.id}>
                  {s.name} · {s.start_time}–{s.end_time} · {s.timezone}
                </MenuItem>)}
              </Select>
            </FormControl>
            {/* Admin-only assignment fields */}
            {isAdmin() && (
              <>
                {form.role === 'supervisor' && locations.length === 0 && (
                  <Alert severity="warning">
                    No locations exist yet. Go to the <strong>Locations</strong> page and create one before adding a supervisor.
                  </Alert>
                )}
                <FormControl size="small">
                  <InputLabel>Location {form.role === 'supervisor' ? '(required for supervisor)' : ''}</InputLabel>
                  <Select value={form.location_id} label="Location"
                    onChange={e => setForm(f => ({ ...f, location_id: e.target.value }))}>
                    <MenuItem value="">None</MenuItem>
                    {locations.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                  </Select>
                </FormControl>
                {form.role === 'operator' && (
                  <FormControl size="small">
                    <InputLabel>Supervisor</InputLabel>
                    <Select value={form.supervisor_id} label="Supervisor"
                      onChange={e => {
                        const supId = e.target.value;
                        const sup   = supervisors.find(s => s.id === supId);
                        setForm(f => ({
                          ...f,
                          supervisor_id: supId,
                          // Auto-assign supervisor's location to this operator
                          location_id: sup?.location_id || f.location_id,
                        }));
                      }}>
                      <MenuItem value="">None</MenuItem>
                      {supervisors.map(s => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name} ({s.employee_id})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit dialog ── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Operator — {selected?.name}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" size="small" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <TextField label="Email" size="small" type="email" value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            <FormControl size="small" required>
              <InputLabel>Shift</InputLabel>
              <Select value={form.shift_id} label="Shift"
                onChange={e => setForm(f => ({ ...f, shift_id: e.target.value }))}>
                <MenuItem value="">Unassigned</MenuItem>
                {shifts.filter(s => s.is_active).map(s =>
                  <MenuItem key={s.id} value={s.id}>
                    {s.name} · {s.start_time}–{s.end_time} · {s.timezone}
                  </MenuItem>)}
              </Select>
            </FormControl>
            {isAdmin() && (
              <>
                <FormControl size="small">
                  <InputLabel>Role</InputLabel>
                  <Select value={form.role} label="Role"
                    onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                    {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small">
                  <InputLabel>Location</InputLabel>
                  <Select value={form.location_id} label="Location"
                    onChange={e => setForm(f => ({ ...f, location_id: e.target.value }))}>
                    <MenuItem value="">None</MenuItem>
                    {locations.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                  </Select>
                </FormControl>
                {form.role === 'operator' && (
                  <FormControl size="small">
                    <InputLabel>Supervisor</InputLabel>
                    <Select value={form.supervisor_id} label="Supervisor"
                      onChange={e => {
                        const supId = e.target.value;
                        const sup   = supervisors.find(s => s.id === supId);
                        setForm(f => ({
                          ...f,
                          supervisor_id: supId,
                          // Auto-assign supervisor's location to this operator
                          location_id: sup?.location_id || f.location_id,
                        }));
                      }}>
                      <MenuItem value="">None</MenuItem>
                      {supervisors.map(s => (
                        <MenuItem key={s.id} value={s.id}>{s.name} ({s.employee_id})</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {form.role === 'supervisor' && (
                  <TextField
                    label="New Dashboard Password (leave blank to keep current)"
                    size="small" type="password"
                    value={form.dashboard_password}
                    onChange={e => setForm(f => ({ ...f, dashboard_password: e.target.value }))}
                  />
                )}
              </>
            )}

          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reset PIN dialog ── */}
      <Dialog open={pinOpen} onClose={() => setPinOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset PIN — {selected?.name}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <TextField
            label="New PIN (4-8 digits)" fullWidth size="small" sx={{ mt: 1 }}
            type="password" value={newPin}
            onChange={e => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
            inputProps={{ maxLength: 8, inputMode: 'numeric' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPinOpen(false)}>Cancel</Button>
          <Button onClick={handleResetPin} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Reset'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Set dashboard password dialog ── */}
      <Dialog open={dashPwOpen} onClose={() => setDashPwOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Set Dashboard Password — {selected?.name}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <TextField
            label="New Password (min 6 chars)" fullWidth size="small" sx={{ mt: 1 }}
            type="password" value={newDashPw}
            onChange={e => setNewDashPw(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDashPwOpen(false)}>Cancel</Button>
          <Button onClick={handleSetDashPw} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Set Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
